import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import AttributionSummaryCard from './components/AttributionSummaryCard';
import AnalyticsDashboardPanel from './components/AnalyticsDashboardPanel';
import ChannelPerformancePanel from './components/ChannelPerformancePanel';
import AttributionModelingPanel from './components/AttributionModelingPanel';
import CampaignEffectivenessPanel from './components/CampaignEffectivenessPanel';
import LeadQualityScoringPanel from './components/LeadQualityScoringPanel';

const LeadSourceAnalyticsAttributionHub = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState('last_30_days');
  const [selectedChannel, setSelectedChannel] = useState('all');
  const [attributionModel, setAttributionModel] = useState('multi_touch');

  // Mock data for attribution summary
  const [summaryData, setSummaryData] = useState({
    totalLeadSources: 12,
    costPerLead: 245.50,
    avgConversionRate: 18.5,
    marketingROI: 325.8,
    trend: 'up'
  });

  // Mock data for channel performance
  const [channelData, setChannelData] = useState([
    {
      id: 1,
      channel: 'Website Inquiries',
      leads: 156,
      conversions: 32,
      conversionRate: 20.5,
      cost: 12450,
      costPerLead: 79.81,
      roi: 285.5,
      quality: 'high'
    },
    {
      id: 2,
      channel: 'Social Media',
      leads: 243,
      conversions: 38,
      conversionRate: 15.6,
      cost: 18900,
      costPerLead: 77.78,
      roi: 198.3,
      quality: 'medium'
    },
    {
      id: 3,
      channel: 'Referral Programs',
      leads: 89,
      conversions: 28,
      conversionRate: 31.5,
      cost: 8500,
      costPerLead: 95.51,
      roi: 412.7,
      quality: 'high'
    },
    {
      id: 4,
      channel: 'Government Outreach',
      leads: 67,
      conversions: 18,
      conversionRate: 26.9,
      cost: 15600,
      costPerLead: 232.84,
      roi: 156.2,
      quality: 'medium'
    },
    {
      id: 5,
      channel: 'Direct Sales',
      leads: 124,
      conversions: 45,
      conversionRate: 36.3,
      cost: 28700,
      costPerLead: 231.45,
      roi: 389.4,
      quality: 'high'
    }
  ]);

  // Mock data for attribution touchpoints
  const [attributionData, setAttributionData] = useState([
    {
      id: 1,
      touchpoint: 'Initial Awareness',
      channel: 'Social Media',
      credit: 15,
      interactions: 243,
      avgTimeToNext: '2.3 days'
    },
    {
      id: 2,
      touchpoint: 'Consideration',
      channel: 'Website Visit',
      credit: 25,
      interactions: 189,
      avgTimeToNext: '5.1 days'
    },
    {
      id: 3,
      touchpoint: 'Evaluation',
      channel: 'Demo Request',
      credit: 30,
      interactions: 124,
      avgTimeToNext: '3.8 days'
    },
    {
      id: 4,
      touchpoint: 'Final Conversion',
      channel: 'Direct Sales',
      credit: 30,
      interactions: 98,
      avgTimeToNext: 'N/A'
    }
  ]);

  // Mock data for campaigns
  const [campaignData, setCampaignData] = useState([
    {
      id: 1,
      name: 'PNG Government HR Compliance',
      status: 'active',
      leads: 145,
      conversions: 28,
      spend: 15600,
      roi: 298.5,
      startDate: '2026-01-01',
      endDate: '2026-03-31'
    },
    {
      id: 2,
      name: 'Payroll Automation Launch',
      status: 'active',
      leads: 198,
      conversions: 42,
      spend: 22400,
      roi: 356.2,
      startDate: '2025-12-15',
      endDate: '2026-02-28'
    },
    {
      id: 3,
      name: 'Employee Self-Service Promo',
      status: 'completed',
      leads: 89,
      conversions: 15,
      spend: 8900,
      roi: 187.3,
      startDate: '2025-11-01',
      endDate: '2025-12-31'
    }
  ]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Data is already set in state
      setLoading(false);
    } catch (err) {
      setError(err?.message || 'Failed to load attribution data');
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [dateRange, selectedChannel, attributionModel]);

  const handleExport = () => {
    console.log('Exporting attribution report...');
    // Export functionality would be implemented here
  };

  if (loading && !summaryData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <GlobalFilterBar onFilterChange={() => {}} />
        <ComplianceAlertBanner />
        <div className="flex items-center justify-center h-96">
          <Icon name="Loader" className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Lead Source Analytics & Attribution Hub - PNG HR Analytics</title>
        <meta name="description" content="Comprehensive marketing attribution analysis with ROI tracking and channel performance optimization" />
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        <Header />
        <GlobalFilterBar onFilterChange={() => {}} />
        <ComplianceAlertBanner />
        <DataRefreshIndicator lastUpdated={new Date()} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                  <Icon name="Target" className="w-8 h-8 text-blue-600" />
                  Lead Source Analytics & Attribution Hub
                </h1>
                <p className="mt-2 text-gray-600">
                  Comprehensive marketing attribution analysis with ROI tracking and channel performance optimization
                </p>
              </div>
              <Button onClick={handleExport} className="flex items-center gap-2">
                <Icon name="Download" className="w-4 h-4" />
                Export Report
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date Range
                </label>
                <Select
                  value={dateRange}
                  onChange={(e) => setDateRange(e?.target?.value)}
                  className="w-full"
                >
                  <option value="last_7_days">Last 7 Days</option>
                  <option value="last_30_days">Last 30 Days</option>
                  <option value="last_90_days">Last 90 Days</option>
                  <option value="last_year">Last Year</option>
                  <option value="custom">Custom Range</option>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Channel Filter
                </label>
                <Select
                  value={selectedChannel}
                  onChange={(e) => setSelectedChannel(e?.target?.value)}
                  className="w-full"
                >
                  <option value="all">All Channels</option>
                  <option value="website">Website Inquiries</option>
                  <option value="social">Social Media</option>
                  <option value="referral">Referral Programs</option>
                  <option value="government">Government Outreach</option>
                  <option value="direct">Direct Sales</option>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Attribution Model
                </label>
                <Select
                  value={attributionModel}
                  onChange={(e) => setAttributionModel(e?.target?.value)}
                  className="w-full"
                >
                  <option value="first_touch">First Touch</option>
                  <option value="last_touch">Last Touch</option>
                  <option value="linear">Linear</option>
                  <option value="time_decay">Time Decay</option>
                  <option value="multi_touch">Multi-Touch</option>
                </Select>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 text-red-800">
                <Icon name="AlertCircle" className="w-5 h-5" />
                <span>{error}</span>
              </div>
            </div>
          )}

          {/* Attribution Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <AttributionSummaryCard
              title="Total Lead Sources"
              value={summaryData?.totalLeadSources}
              icon="Database"
              trend={summaryData?.trend}
              subtitle=""
            />
            <AttributionSummaryCard
              title="Cost Per Lead"
              value={`K${summaryData?.costPerLead?.toFixed(2)}`}
              icon="DollarSign"
              trend={summaryData?.trend === 'up' ? 'down' : 'up'}
              subtitle="PNG Kina"
            />
            <AttributionSummaryCard
              title="Avg Conversion Rate"
              value={`${summaryData?.avgConversionRate}%`}
              icon="TrendingUp"
              trend={summaryData?.trend}
              subtitle=""
            />
            <AttributionSummaryCard
              title="Marketing ROI"
              value={`${summaryData?.marketingROI}%`}
              icon="Target"
              trend={summaryData?.trend}
              subtitle=""
            />
          </div>

          {/* Main Analytics Dashboard */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-2">
              <AnalyticsDashboardPanel
                channelData={channelData}
                attributionData={attributionData}
                dateRange={dateRange}
              />
            </div>
            <div>
              <LeadQualityScoringPanel channelData={channelData} />
            </div>
          </div>

          {/* Channel Performance */}
          <div className="mb-6">
            <ChannelPerformancePanel
              channelData={channelData}
              selectedChannel={selectedChannel}
            />
          </div>

          {/* Attribution Modeling */}
          <div className="mb-6">
            <AttributionModelingPanel
              attributionData={attributionData}
              attributionModel={attributionModel}
            />
          </div>

          {/* Campaign Effectiveness */}
          <div className="mb-6">
            <CampaignEffectivenessPanel
              campaignData={campaignData}
              dateRange={dateRange}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default LeadSourceAnalyticsAttributionHub;